import { styled } from '@glitz/react';
import React, { useRef, useEffect }  from 'react';
import Link from 'Shared/Link';
import { TextLabel } from './Typography';
import { Down12x12, Right12x12 } from 'Shared/Icons/Chevron';
import BreadcrumbsViewModel from 'Shared/Breadcrumbs/BreadcrumbsViewModel.type';
import * as style from 'Shared/Style';

const BaseWrap = styled.div({
  textDecoration: 'none',
  display: 'flex',
  flexDirection: 'row',
  padding: { xy: '0px' },
})

const VerticalWrap = styled(BaseWrap, {
  transform: 'rotate(180deg)',
  justifyContent: 'center',
  writingMode: 'vertical-rl',
  alignItems: 'start',
  ['@media ' + style.mediaUpToLarge]: {
    padding: {
      x: '12px'
    }
  },
})

const HorizontalWrap = styled(BaseWrap, {
  alignItems: 'center',
  flexWrap: 'nowrap',
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  //@ts-ignore
  '-ms-overflow-style': 'none',
  '::-webkit-scrollbar': {
    display: 'none'
  },
  ['@media ' + style.mediaUpToLarge]: {
    padding: {
      x: '12px'
    }
  },
})

const ListWrap = styled.div({
  display: 'flex',
  alignItems: 'center',
})

const ListItem = styled.div({
  display: 'flex',
  gap: '2px',
  alignItems: 'center',
})

const BreadCrumbLink = styled(Link, {
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  padding: { xy: '0px' },
})

export const Breadcrumbs = (props: { breadcrumbs: BreadcrumbsViewModel[]; vertical?: boolean; white?: boolean, isProductPage?: boolean, isCompact?: boolean }) => {
  const { vertical, breadcrumbs, white, isCompact, isProductPage } = props;
  const opaqueColor = white ? style.colors.monochrome.white : style.colors.monochrome.black;
  const halfTransparentColor = white
    ? style.colors.rgbaFilters.white50
    : style.colors.rgbaFilters.black50;

  if (breadcrumbs?.length < 1) {
    return <></>;
  }

  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (vertical) {
      return () => {}
    }
    if(containerRef && containerRef.current !== null) {
      containerRef.current.scrollLeft = containerRef.current.offsetWidth; 
    }

    return () => {} 
  }, [breadcrumbs])
  const Wrapper = vertical ? VerticalWrap : HorizontalWrap;
  return (
    <Wrapper ref={containerRef}>
      <ListWrap
        itemType="http://schema.org/BreadcrumbList"
        itemScope
      >
      {breadcrumbs?.map((crumb, index, array) => {
        return (
          <ListItem key={index}>
            {index !== 0 &&
              (vertical ? (
                <Down12x12 
                  css={{
                    color: index === array.length - 1 ? opaqueColor : halfTransparentColor,
                  }}
                />
              ) : (
                <Right12x12 
                css={{
                  color: index === array.length - 1 ? opaqueColor : halfTransparentColor,
                }}
                />
              )
              )
            }
            <div
              key={index}
              itemProp="itemListElement"
                itemType="http://schema.org/ListItem"
                itemScope
            >
              <BreadCrumbLink
                css={{
                  color: index === array.length - 1 ? opaqueColor : halfTransparentColor,
                }}
                to={crumb.url}
                title={crumb.text}
                key={index}
                itemProp="item" 
              >
                <TextLabel>{crumb.text}</TextLabel>  
              </BreadCrumbLink>
              <meta itemProp="position" content={String(index + 1)} />
              <meta itemProp="name" content={crumb.text} />
              <meta itemProp="url" content={`${window.location.protocol}//${window.location.host}${crumb.url}`} />
            </div>
          </ListItem>
        );
      })}
      </ListWrap>
    </Wrapper>
  );
};

export default Breadcrumbs;
